<script setup lang="ts">
/**
 * Cloudflare Turnstile widget (explicit rendering).
 *
 * Loads the Turnstile script once and renders an invisible/managed widget.
 * The resolved token is exposed through v-model and is single-use: after a
 * successful form submit the parent should call `reset()` (exposed below).
 *
 * Turnstile is a security mechanism, not a tracker, so it can load without
 * cookie consent (see plugins/consent.client.ts for the tracking scripts).
 */

const props = withDefaults(
    defineProps<{
        siteKey: string
        theme?: "light" | "dark" | "auto"
        size?: "normal" | "compact" | "flexible"
    }>(),
    {
        theme: "auto",
        size: "flexible",
    }
)

const emit = defineEmits<{
    (e: "verified", token: string): void
    (e: "expired"): void
    (e: "error"): void
}>()

const token = defineModel<string>({ default: "" })

const SCRIPT_SRC =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

const container = ref<HTMLElement | null>(null)
let widgetId: string | undefined

function loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window === "undefined") return resolve()
        if (window.turnstile) return resolve()

        const existing = document.querySelector<HTMLScriptElement>(
            `script[src="${SCRIPT_SRC}"]`
        )
        if (existing) {
            existing.addEventListener("load", () => resolve())
            existing.addEventListener("error", () => reject())
            return
        }

        const script = document.createElement("script")
        script.src = SCRIPT_SRC
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject()
        document.head.appendChild(script)
    })
}

function renderWidget() {
    if (!window.turnstile || !container.value || widgetId !== undefined) return

    widgetId = window.turnstile.render(container.value, {
        sitekey: props.siteKey,
        theme: props.theme,
        size: props.size,
        callback: (value: string) => {
            token.value = value
            emit("verified", value)
        },
        "expired-callback": () => {
            token.value = ""
            emit("expired")
        },
        "error-callback": () => {
            token.value = ""
            emit("error")
        },
    })
}

function reset() {
    token.value = ""
    if (window.turnstile && widgetId !== undefined) {
        window.turnstile.reset(widgetId)
    }
}

onMounted(async () => {
    try {
        await loadScript()
    } catch {
        emit("error")
        return
    }

    // The script's load event guarantees `window.turnstile` is initialized, so
    // we render directly. We must NOT call `turnstile.ready()` here: it throws
    // when api.js was injected with async/defer (which it is).
    renderWidget()
})

onBeforeUnmount(() => {
    if (window.turnstile && widgetId !== undefined) {
        window.turnstile.remove(widgetId)
        widgetId = undefined
    }
})

defineExpose({ reset })
</script>

<template>
    <div ref="container" />
</template>
